import {
  FlushResultDto,
  FlushSpecificationDto,
  FlushStageDto,
  FlushWarningDto
} from 'providers/api';
import { pumpPressureBarToKpa, pumpPressureKpaToBar } from 'utils/unitConversions';
import {
  FlushResult,
  FlushSpec,
  FlushStage,
  FlushWarning,
  FlushWarningCode
} from './flushTypes';

// The solver works in kPa; the persisted DTOs keep pressures in bar like every other recipe value.
// These are the only two places the conversion happens.

const isFiniteNumber = (value: number | null | undefined): value is number => typeof value === 'number' && Number.isFinite(value);

const optional = (value: number | null | undefined): number | undefined => (isFiniteNumber(value) ? value : undefined);

export const toFlushSpec = (dto: FlushSpecificationDto): FlushSpec => ({
  flushPumpMaxPressureKpa: pumpPressureBarToKpa(dto.flushPumpMaxPressure),
  flushPumpFlowRate: dto.flushPumpFlowRate,
  pastePumpFlowRate: dto.pastePumpFlowRate,
  airFlushEnabled: dto.airFlushEnabled,
  airFlushMaxPressureKpa: isFiniteNumber(dto.airFlushMaxPressure) ? pumpPressureBarToKpa(dto.airFlushMaxPressure) : undefined,
  volumeMode: dto.volumeMode,
  volumeValue: dto.volumeValue,
});

export const toFlushSpecificationDto = (spec: FlushSpec): FlushSpecificationDto => ({
  flushPumpMaxPressure: pumpPressureKpaToBar(spec.flushPumpMaxPressureKpa),
  flushPumpFlowRate: spec.flushPumpFlowRate,
  pastePumpFlowRate: spec.pastePumpFlowRate,
  airFlushEnabled: spec.airFlushEnabled,
  airFlushMaxPressure: spec.airFlushEnabled && isFiniteNumber(spec.airFlushMaxPressureKpa) ? pumpPressureKpaToBar(spec.airFlushMaxPressureKpa) : undefined,
  volumeMode: spec.volumeMode,
  volumeValue: spec.volumeValue,
});

const toStageDto = (stage: FlushStage): FlushStageDto => ({
  stageType: stage.stageType,
  startVolume: stage.startVolume,
  endVolume: stage.endVolume,
  startDistance: stage.startDistance,
  endDistance: stage.endDistance,
  flowRate: stage.flowRate,
  requiredPressureStart: pumpPressureKpaToBar(stage.requiredPressureStartKpa),
  requiredPressureEnd: pumpPressureKpaToBar(stage.requiredPressureEndKpa),
  durationMinutes: stage.durationMinutes,
});

const fromStageDto = (dto: FlushStageDto): FlushStage => ({
  stageType: dto.stageType,
  startVolume: dto.startVolume,
  endVolume: dto.endVolume,
  startDistance: dto.startDistance,
  endDistance: dto.endDistance,
  flowRate: dto.flowRate,
  requiredPressureStartKpa: pumpPressureBarToKpa(dto.requiredPressureStart),
  requiredPressureEndKpa: pumpPressureBarToKpa(dto.requiredPressureEnd),
  durationMinutes: dto.durationMinutes,
});

const toWarningDto = (warning: FlushWarning): FlushWarningDto => ({ code: warning.code, description: warning.description });

const fromWarningDto = (dto: FlushWarningDto): FlushWarning => ({ code: dto.code as FlushWarningCode, description: dto.description ?? '' });

export const toFlushResultDto = (result: FlushResult): FlushResultDto => ({
  totalLineVolume: result.totalLineVolume,
  totalLineLength: result.totalLineLength,
  finalFlushVolume: result.finalFlushVolume,
  pastePumpWaterVolume: result.pastePumpWaterVolume,
  pastePumpHandoverDistance: result.pastePumpHandoverDistance,
  flushPumpCanCompleteAlone: result.flushPumpCanCompleteAlone,
  airStartWaterVolume: result.airStartWaterVolume,
  airStartDistance: result.airStartDistance,
  airStartTimeMinutes: result.airStartTimeMinutes,
  airStartStage: result.airStartStage,
  timeToWaterAtStopeMinutes: result.timeToWaterAtStopeMinutes,
  waterAtStopeStage: result.waterAtStopeStage,
  waterFrontStopDistance: result.waterFrontStopDistance,
  maxRequiredPressure: pumpPressureKpaToBar(result.maxRequiredPressureKpa),
  stages: result.stages.map(toStageDto),
  warnings: result.warnings.map(toWarningDto),
});

// Optional values come back from the API as null; the solver types use undefined.
export const fromFlushResultDto = (dto: FlushResultDto): FlushResult => ({
  totalLineVolume: dto.totalLineVolume,
  totalLineLength: dto.totalLineLength,
  finalFlushVolume: dto.finalFlushVolume,
  pastePumpWaterVolume: dto.pastePumpWaterVolume,
  pastePumpHandoverDistance: dto.pastePumpHandoverDistance,
  flushPumpCanCompleteAlone: dto.flushPumpCanCompleteAlone,
  airStartWaterVolume: optional(dto.airStartWaterVolume),
  airStartDistance: optional(dto.airStartDistance),
  airStartTimeMinutes: optional(dto.airStartTimeMinutes),
  airStartStage: dto.airStartStage ?? undefined,
  timeToWaterAtStopeMinutes: optional(dto.timeToWaterAtStopeMinutes),
  waterAtStopeStage: dto.waterAtStopeStage ?? undefined,
  waterFrontStopDistance: dto.waterFrontStopDistance,
  maxRequiredPressureKpa: pumpPressureBarToKpa(dto.maxRequiredPressure),
  stages: (dto.stages ?? []).map(fromStageDto),
  warnings: (dto.warnings ?? []).map(fromWarningDto),
});
